import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {type HeadcountRollup, WorkstreamHeadcountDataService} from 'client';
import {
  formatStaffingCents,
  parseStaffingCents,
  type StaffingField,
  type StaffingValues,
} from 'helpers/staffingValues';
import {t} from 'i18next';
import {getService} from 'utilities';

import styles from './WorkstreamDetailsShell.module.css';

export const EMPTY_VALUE = '—';

const formatCents = (cents: number | null) =>
  cents == null ? EMPTY_VALUE : formatStaffingCents(cents);

// Legacy percent-mode entries can normalize to more than two decimal places.
export const formatAmount = (value: string) =>
  formatCents(parseStaffingCents(String(Math.round(Number(value) * 100) / 100)));

const toSummaryValues = (summary: HeadcountRollup): StaffingValues => ({
  geo_low_value: summary.geo_low_fte,
  geo_mid_value: summary.geo_mid_fte,
  geo_high_value: summary.geo_high_fte,
  allocation_growth_value: summary.allocation_growth_fte,
  allocation_ci_value: summary.allocation_ci_fte,
  allocation_ktlo_value: summary.allocation_ktlo_fte,
});

const Group = ({
  label,
  values,
  fields,
}: {
  label: string;
  values: StaffingValues | null;
  fields: Array<[string, StaffingField]>;
}) => (
  <Box display="flex" flexDirection="column" rowGap="8" className={styles.staffingGroup}>
    <Text size="small" isBold className={styles.staffingGroupHeading}>
      {label}
    </Text>
    <dl className={styles.staffingValueList}>
      {fields.map(([fieldLabel, key]) => (
        <div key={key} className={styles.staffingValueRow}>
          <Text tagName="dt" size="small">
            {fieldLabel}
          </Text>
          <Text tagName="dd" size="small" className={styles.staffingValue}>
            {values ? formatAmount(values[key]) : EMPTY_VALUE}
          </Text>
        </div>
      ))}
    </dl>
  </Box>
);

export const ALLOCATION_FIELDS = (): Array<[string, StaffingField]> => [
  [t('workstream_staffing_allocation_growth'), 'allocation_growth_value'],
  [t('workstream_staffing_allocation_ci'), 'allocation_ci_value'],
  [t('workstream_staffing_allocation_ktlo'), 'allocation_ktlo_value'],
];

export const GEO_FIELDS = (): Array<[string, StaffingField]> => [
  [t('workstream_staffing_geo_low'), 'geo_low_value'],
  [t('workstream_staffing_geo_mid'), 'geo_mid_value'],
  [t('workstream_staffing_geo_high'), 'geo_high_value'],
];

export type WorkstreamStaffingSummary = {values: StaffingValues | null; headcount: string};

export type WorkstreamStaffingState =
  | {status: 'loading'}
  // In no pillar of the half: outside planning, so there's no staffing to show.
  | {status: 'outside_half'}
  | {status: 'denied'}
  | {status: 'error'}
  | {status: 'ready'; staffing: WorkstreamStaffingSummary};

/**
 * The workstream's staffing for the half, rolled up from the workstreams below it across all their
 * pillars, and why it's missing when it is: still loading, the workstream is outside the half, the
 * viewer can't read it (or the query is disabled), or the request failed. `values` is null when
 * nothing has been entered.
 */
export const useWorkstreamStaffingState = (
  workstreamId: number,
  enabled = true
): WorkstreamStaffingState => {
  const query = useQuery({
    queryKey: ['workstream-headcount-data', workstreamId],
    queryFn: () =>
      getService(
        WorkstreamHeadcountDataService
      ).getWorkstreamHeadcountDataApiV1WorkstreamWorkstreamIdHeadcountDataGet(workstreamId),
    enabled,
  });

  if (query.isLoading) return {status: 'loading'};
  // A failed refetch keeps the cached data, so it's an error only with nothing to show.
  if (!query.data) return query.isError ? {status: 'error'} : {status: 'denied'};
  // Only an explicit false: an API from before in_half (e.g. while a deploy rolls out) omits it.
  if (query.data.in_half === false) return {status: 'outside_half'};
  if (!query.data.can_read) return {status: 'denied'};

  const summary = query.data.summary;
  return {
    status: 'ready',
    staffing: {
      values: summary ? toSummaryValues(summary) : null,
      headcount: summary ? formatAmount(summary.headcount_fte) : EMPTY_VALUE,
    },
  };
};

/**
 * The workstream's staffing (see useWorkstreamStaffingState), or null while loading, when
 * disabled, outside the half, or when the viewer can't read it.
 */
export const useWorkstreamStaffing = (
  workstreamId: number,
  enabled = true
): WorkstreamStaffingSummary | null => {
  const state = useWorkstreamStaffingState(workstreamId, enabled);
  return state.status === 'ready' ? state.staffing : null;
};

/**
 * Read-only summary of a workstream's staffing, summed across the pillars it belongs to; values
 * are edited per pillar on the wireframe data page.
 */
export const WorkstreamStaffing = ({workstreamId}: {workstreamId: number}) => {
  const staffing = useWorkstreamStaffing(workstreamId);
  if (!staffing) return null;
  const {values, headcount} = staffing;

  return (
    <section className={styles.sidebarSection} aria-label={t('workstream_staffing')}>
      <Text className={styles.staffingEyebrow} size="small" isBold>
        {t('workstream_staffing')}
      </Text>
      <Box display="flex" flexDirection="column" rowGap="16" className={styles.sidebarSectionBody}>
        <dl className={styles.staffingValueList}>
          <div className={styles.staffingValueRow}>
            <Text tagName="dt" size="small">
              {t('workstream_staffing_headcount')}
            </Text>
            <Text tagName="dd" size="small" isBold className={styles.staffingValue}>
              {headcount}
            </Text>
          </div>
        </dl>
        <Box className={styles.staffingColumns}>
          <Group
            label={t('workstream_staffing_allocation')}
            values={values}
            fields={ALLOCATION_FIELDS()}
          />
          <Group label={t('workstream_staffing_geos')} values={values} fields={GEO_FIELDS()} />
        </Box>
      </Box>
    </section>
  );
};
